import React from "react";
import { Button, IconButton, Typography, useTheme } from "@mui/material";
import WatchlistIcon from "src/icons/WatchlistIcon";
import { buttonStyle, buttonTextStyle } from "../coins/CoinButtonGroup";
import { useMarketsState } from "src/store/features/markets.slice";
import { useWatchlist } from "src/hooks/useWatchlist";
import { CurrencyConfigRes } from "src/store/features/api/generated";

interface WatchToggleProps {
  code: string;
  height?: string;
  width?: string;
  watched?: boolean; // Deprecated - kept for backward compatibility but not used
  variant: "button" | "icon";
  color?: string;
  currencyConfig?: CurrencyConfigRes; // Optional currency config for optimistic updates
}

const WatchToggle = React.memo(
  ({
    code,
    width,
    height,
    variant,
    color,
    currencyConfig: propCurrencyConfig,
  }: WatchToggleProps) => {
    const theme = useTheme();
    const { coinStatistics } = useMarketsState();

    // Use watchlist hook for managing watchlist operations
    const { toggleWatchlist, isCoinInWatchlist } = useWatchlist({
      trackingContext: "Markets",
    });

    // Determine if coin is being watched using the hook
    const isWatching = isCoinInWatchlist(code);

    // Get currencyConfig from prop, markets slice, or undefined (for optimistic add)
    // Priority: prop > markets slice > undefined
    const currencyConfig =
      propCurrencyConfig ||
      (coinStatistics?.currencyConfig?.code === code ? coinStatistics.currencyConfig : undefined);

    const handleAction = () => {
      // Use the hook to toggle watchlist status
      // The hook handles optimistic updates, API calls, and error handling
      toggleWatchlist(code, currencyConfig);
    };

    return (
      <>
        {isWatching ? (
          variant === "icon" ? (
            <IconButton onClick={() => handleAction()}>
              <WatchlistIcon
                watch={true}
                width={width}
                height={height}
                color={color ? color : theme.palette.primary.dark}
              />
            </IconButton>
          ) : (
            <Button
              disableRipple
              sx={buttonStyle}
              endIcon={
                <WatchlistIcon
                  watch={true}
                  width={width}
                  height={height}
                  color={theme.palette.primary.dark}
                />
              }
              onClick={() => handleAction()}>
              <Typography sx={buttonTextStyle}>{"Watchlist"}</Typography>
            </Button>
          )
        ) : variant === "icon" ? (
          <IconButton onClick={() => handleAction()}>
            <WatchlistIcon
              watch={false}
              width={width}
              height={height}
              color={color ? color : theme.palette.primary.dark}
            />
          </IconButton>
        ) : (
          <Button
            disableRipple
            sx={buttonStyle}
            endIcon={
              <WatchlistIcon
                watch={false}
                width={width}
                height={height}
                color={color ? color : theme.palette.primary.dark}
              />
            }
            onClick={() => handleAction()}>
            <Typography sx={buttonTextStyle}>{"Watchlist"}</Typography>
          </Button>
        )}
      </>
    );
  },
);

export default WatchToggle;
